// ============================================================================
// Admin Types - Product Catalog, Customer & Order Management
// ============================================================================

// ----------------------------------------------------------------------------
// Address Types
// ----------------------------------------------------------------------------

export interface Address {
  street: string | null
  city: string | null
  state: string | null
  zip: string | null
  country: string | null
}

// ----------------------------------------------------------------------------
// Product Catalog (expanded product model)
// ----------------------------------------------------------------------------

export interface CatalogProduct {
  id: number
  sku: string
  product_type: string
  short_description: string
  description: string | null
  category: string | null
  category_image_url: string | null
  manufacturer: string | null
  manufacturer_image_url: string | null
  price: number | null
  sale_price: number | null
  image_url: string | null
  image_url_2: string | null
  image_url_3: string | null
  related_product_ids: number[] | null
  custom_attributes: Record<string, unknown> | null
  is_active: boolean
  created_at: string | null
  updated_at: string | null
}

export interface CatalogProductListResponse {
  products: CatalogProduct[]
  total: number
  pages: number
  current_page: number
}

export interface CatalogProductOptions {
  categories: Array<{ id: number; name: string; image_url: string | null }>
  manufacturers: Array<{ id: number; name: string; image_url: string | null }>
}

export interface CatalogProductFormData {
  sku: string
  product_type: string
  category: string
  short_description?: string
  description?: string
  manufacturer?: string
  manufacturer_image_url?: string
  price?: number
  sale_price?: number
  image_url?: string | null
  image_url_2?: string | null
  image_url_3?: string | null
  related_product_ids?: number[] | null
  custom_attributes?: Record<string, unknown>
  is_active?: boolean
}

// Legacy alias for backwards compatibility
export type InventoryProduct = CatalogProduct
export type InventoryProductListResponse = CatalogProductListResponse
export type InventoryProductFormData = CatalogProductFormData

// ----------------------------------------------------------------------------
// Customer Types (Non-admin users with business fields)
// ----------------------------------------------------------------------------

export type AccountStatus = 'invited' | 'active' | 'credit_hold' | 'suspended' | 'inactive'

export interface Customer {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  company: string | null
  phone_number: string | null
  is_active: boolean
  is_admin: boolean
  twofa_enabled?: boolean
  created_at: string | null
  // Customer-specific fields - billing address only (shipping is per-order)
  billing_street: string | null
  billing_city: string | null
  billing_state: string | null
  billing_zip: string | null
  billing_country: string | null
  account_status: AccountStatus | null
  customer_notes: string | null
  credit_limit: number
  available_credit?: number | null
  used_credit?: number | null
  payment_terms: string | null
  require_po_number: boolean
  shortage_preference: string
}

export interface CustomerSecurity {
  last_login: string | null
  last_login_ip: string | null
  last_login_location: string | null
  last_login_browser: string | null
  last_login_device: string | null
  failed_login_attempts: number
  last_failed_login: string | null
  password_reset_count: number
  rate_limit_violations: number
  locked_until: string | null
  twofa_enabled: boolean
}

export interface CustomerListResponse {
  customers: Customer[]
  total: number
  pages: number
  current_page: number
}

// ----------------------------------------------------------------------------
// Order Types
// ----------------------------------------------------------------------------

export type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'

export interface OrderItem {
  id: number
  order_id: number
  product_id: number | null
  product_name: string | null
  product_sku: string | null
  quantity: number
  fulfilled_quantity: number | null
  unit_price: number
  line_total: number
  product?: {
    id: number
    sku: string
    name: string
    image_url: string | null
    category: string | null
  }
}

export interface OrderCustomer {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  company: string | null
}

export interface Order {
  id: number
  order_number: string
  customer_id: number | null
  customer_name: string | null
  customer_email: string | null
  customer_company: string | null
  status: OrderStatus
  status_changed_at: string | null
  status_changed_by_id: number | null
  shipping_address: Address
  billing_address: Address
  subtotal: number
  tax: number
  shipping_cost: number
  total: number
  notes: string | null
  admin_notes: string | null
  po_number: string | null
  parent_order_id: number | null
  parent_order_number: string | null
  is_backorder: boolean
  tracking_number: string | null
  carrier: string | null
  easypost_tracker_id: string | null
  requested_delivery_date: string | null
  expected_delivery_date: string | null
  created_at: string | null
  updated_at: string | null
  item_count?: number
  items?: OrderItem[]
  customer?: OrderCustomer
  invoice_id?: number | null
  invoice_number?: string | null
  invoice_has_pdf?: boolean
  backorders?: { id: number; order_number: string; status: string }[]
}

export interface Invoice {
  id: number
  invoice_number: string
  customer_id: number | null
  order_id: number | null
  customer_name: string | null
  customer_email: string | null
  customer_company: string | null
  order_subtotal: number
  order_tax: number
  order_shipping: number
  order_total: number
  credit_limit_at_delivery: number | null
  credit_used_before: number | null
  credit_used_after: number | null
  issued_at: string | null
  has_pdf: boolean
}

export interface InvoiceListResponse {
  invoices: Invoice[]
  total: number
  pages: number
  current_page: number
}

export interface OrderListResponse {
  orders: Order[]
  total: number
  pages: number
  current_page: number
}

// ----------------------------------------------------------------------------
// Order Message Types
// ----------------------------------------------------------------------------

export interface OrderMessage {
  id: number
  order_id: number
  sender_id: number
  content: string
  is_admin: boolean
  read_at: string | null
  created_at: string
  sender_name: string
}

export interface OrderMessageListResponse {
  messages: OrderMessage[]
  total: number
  order_status: string
  is_terminal: boolean
}

export interface OrderMessageInboxItem {
  order_id: number
  order_number: string
  order_status: string
  customer_name: string
  last_message_preview: string
  last_message_at: string | null
  last_message_is_admin: boolean | null
  unread_count: number
  message_count: number
}

export interface OrderMessageInboxResponse {
  inbox: OrderMessageInboxItem[]
  total: number
}

// ----------------------------------------------------------------------------
// Manufacturer Types
// ----------------------------------------------------------------------------

export interface Manufacturer {
  id: number
  name: string
  image_url: string | null
  website: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ManufacturerFormData {
  name: string
  image_url?: string | null
  website?: string
}

export interface ManufacturerListResponse {
  manufacturers: Manufacturer[]
  total: number
  pages: number
  current_page: number
}

export interface PublicManufacturer {
  name: string
  image_url: string
  website: string | null
}

// ----------------------------------------------------------------------------
// Form Data Types (for creating/updating)
// ----------------------------------------------------------------------------

export interface CustomerFormData {
  email: string
  password?: string
  first_name?: string
  last_name?: string
  company?: string
  phone_number?: string
  billing_street?: string
  billing_street2?: string
  billing_city?: string
  billing_state?: string
  billing_zip?: string
  billing_country?: string
  account_status?: AccountStatus
  customer_notes?: string
  credit_limit: number
  require_po_number?: boolean
  shortage_preference?: string
  payment_terms?: string | null
}

export interface OrderItemInput {
  product_id: number
  quantity: number
  unit_price?: number
}

export interface OrderFormData {
  customer_id: number
  items: OrderItemInput[]
  shipping_street?: string
  shipping_street2?: string
  shipping_city?: string
  shipping_state?: string
  shipping_zip?: string
  shipping_country?: string
  billing_street?: string
  billing_street2?: string
  billing_city?: string
  billing_state?: string
  billing_zip?: string
  billing_country?: string
  tax?: number
  shipping_cost?: number
  notes?: string
  admin_notes?: string
  po_number?: string
}

// ----------------------------------------------------------------------------
// Featured Products - Links to Product Catalog
// ----------------------------------------------------------------------------

export interface FeaturedProduct {
  id: number | null
  slot_number: number
  product_id: number | null
  product: CatalogProduct | null
  // Sale price mirrors product.sale_price (single source of truth)
  sale_price: number | null
  // Display text
  promo_text: string | null
  custom_description: string | null
  custom_details: string | null
  is_active: boolean
  // Convenience fields (merged from product)
  short_description: string | null
  product_type: string | null
  sku: string | null
  category: string | null
  manufacturer: string | null
  manufacturer_image_url: string | null
  image_url: string | null
  description: string | null
  details: string | null
  created_at?: string | null
  updated_at?: string | null
}

export function isFeaturedProduct(
  product: CatalogProduct | FeaturedProduct
): product is FeaturedProduct {
  return 'product_id' in product
}

export interface FeaturedProductOptions {
  products: CatalogProduct[]
}

export interface FeaturedProductFormData {
  product_id: number
  promo_text?: string
  custom_description?: string
  custom_details?: string
  is_active?: boolean
}

// ----------------------------------------------------------------------------
// Site Settings Types
// ----------------------------------------------------------------------------

export interface ThemePalette {
  id: string
  primary: string
  primaryDark: string
  primaryLight: string
  accent: string
  accentDark: string
  accentLight: string
}

export interface DesignConfig {
  radius: string
  radiusSm: string
  radiusPill: string
  radiusXs: string
  supportedThemes: string[]
  featuredThemes: string[]
  themeDecorations: Record<string, string>
}

export interface SiteSettings {
  id: number
  // Storefront — hidden item lists
  slider_hidden_manufacturer_ids: number[]
  category_section_hidden_category_ids: number[]
  // Storefront toggles
  notification_enabled: boolean
  notification_message: string
  notification_variant: 'primary' | 'accent' | 'warning'
  landing_featured_products_carousel_enabled: boolean
  landing_manufacturers_slider_enabled: boolean
  products_page_header_eyebrow: string | null
  products_page_header_title: string
  products_page_header_subtitle: string
  products_featured_products_carousel_enabled: boolean
  products_manufacturers_slider_enabled: boolean
  // Branding
  company_name: string | null
  company_tagline: string | null
  contact_phone: string | null
  contact_email: string | null
  contact_address_street: string | null
  contact_address_city: string | null
  contact_address_state: string | null
  contact_address_zip: string | null
  contact_hours: string | null
  contact_region: string | null
  contact_maps_url: string | null
  contact_maps_embed_url: string | null
  social_facebook: string | null
  social_instagram: string | null
  social_linkedin: string | null
  theme_id: string
  theme: ThemePalette
  document_theme_id: string
  document_theme: ThemePalette
  navbar_logo: string | null
  hero_logo: string | null
  // Landing page
  hero_eyebrow: string | null
  hero_headline: string | null
  hero_subheading: string | null
  hero_cta_primary_label: string | null
  hero_cta_primary_icon: string | null
  hero_cta_primary_path: string | null
  hero_cta_secondary_label: string | null
  hero_cta_secondary_icon: string | null
  hero_cta_secondary_path: string | null
  // About page
  about_page_eyebrow: string | null
  about_page_title: string | null
  about_lead_text: string | null
  about_stat_1_value: string | null
  about_stat_1_description: string | null
  about_stat_1_show_plus: boolean
  about_stat_2_value: string | null
  about_stat_2_description: string | null
  about_stat_2_show_plus: boolean
  about_stat_3_value: string | null
  about_stat_3_description: string | null
  about_stat_3_show_plus: boolean
  about_faq: Array<{ question: string; answer: string; sort_order?: number }>
  about_closing_text: string | null
  about_show_benefits: boolean
  // Landing page — Category section
  category_section_eyebrow: string | null
  category_section_title: string | null
  category_section_subtitle: string | null
  // Landing page — Benefits section
  benefits_eyebrow: string | null
  benefits_title: string | null
  benefits_subtitle: string | null
  benefit_1_icon: string | null
  benefit_1_title: string | null
  benefit_1_text: string | null
  benefit_2_icon: string | null
  benefit_2_title: string | null
  benefit_2_text: string | null
  benefit_3_icon: string | null
  benefit_3_title: string | null
  benefit_3_text: string | null
  // Contact page
  contact_page_eyebrow: string | null
  contact_page_title: string | null
  contact_page_subtitle: string | null
  contact_part_inquiry_placeholder: string | null
  updated_at: string | null
}

export interface PublicSiteSettings extends SiteSettings {
  demo_mode: boolean
}

// ----------------------------------------------------------------------------
// Product Type Types (sub-categories that belong to a Category)
// ----------------------------------------------------------------------------

export interface ProductType {
  id: number
  name: string
  category_id: number
  category: string | null
  product_count?: number
  created_at: string | null
  updated_at: string | null
}

export interface ProductTypeListResponse {
  product_types: ProductType[]
}

// ----------------------------------------------------------------------------
// Category Types (first-class categories with optional image + description)
// ----------------------------------------------------------------------------

export interface Category {
  id: number
  name: string
  image_url: string | null
  description: string | null
  product_count: number
  product_type_count: number
  created_at: string | null
  updated_at: string | null
}

export interface PublicCategory {
  name: string
  image_url: string | null
  description: string | null
}

// ----------------------------------------------------------------------------
// Product Literature Types
// ----------------------------------------------------------------------------

export interface ProductLiterature {
  id: number
  title: string
  description: string | null
  pdf_url: string
  thumbnail_url: string | null
  manufacturer_id: number | null
  manufacturer_name: string | null
  manufacturer_image_url: string | null
  category_id: number | null
  category_name: string | null
  product_id: number | null
  product_name: string | null
  created_at: string | null
  updated_at: string | null
  scope?: 'product' | 'manufacturer_category' | 'manufacturer' | 'category'
}

export interface ProductLiteratureFormData {
  title: string
  description?: string
  pdf_url: string
  thumbnail_url?: string
  manufacturer_id?: number | null
  category_id?: number | null
  product_id?: number | null
}

export interface ProductLiteratureListResponse {
  literature: ProductLiterature[]
  total: number
  pages: number
  current_page: number
}

// ----------------------------------------------------------------------------
// Notification Types
// ----------------------------------------------------------------------------

export type NotificationType =
  | 'order_placed'
  | 'order_status_changed'
  | 'order_message_received'
  | 'account_updated'
  | 'new_order'
  | 'customer_message'
  | 'new_customer'
  | 'security_alert'

export interface Notification {
  id: number
  user_id: number
  type: NotificationType
  title: string
  body: string
  link: string | null
  related_order_id: number | null
  related_customer_id: number | null
  email_sent: boolean
  is_read: boolean
  read_at: string | null
  created_at: string
}

export interface NotificationListResponse {
  notifications: Notification[]
  total: number
  pages: number
  current_page: number
}

export interface NotificationUnreadCountResponse {
  unread_count: number
}

// ----------------------------------------------------------------------------
// Audit Log Types
// ----------------------------------------------------------------------------

export interface AuditLog {
  id: number
  admin_email: string
  action: string
  target_user_email: string | null
  details: string | null
  ip_address: string
  created_at: string
}
